

/*
 * author: Michael Bauer (Geoplex)
 * email:  michael.bauer@geoplex.com.au
 *
 */

import { subclass, property } from "@arcgis/core/core/accessorSupport/decorators";
import Widget from "@arcgis/core/widgets/Widget";
import { tsx } from "@arcgis/core/widgets/support/widget";
import { Layer, getLayers } from "./layers"
import { dateParse, dateFromStr, dateToString, DateDiff } from "./dates"

import { appConfig } from "./main"

const CSS = {
  base: "timeslider"
};

@subclass("esri.widgets.TimeSlider")
class TimeSlider extends Widget {
    @property()
    min: number = 0


    @property()
    max: number = 12

    @property()
    value: number = 11

    @property()
    step: number = 1

    @property()
    labelstep: number = 1

    @property()
    labels: string[] = []

    @property()
    animating: boolean = false

    @property()
    datetime: string = "None"

    shortdatetime: string = "None"

    @property()
    animatedelay: number = 2000; // animation delay in ms

    @property()
    looping: boolean = true; // animation continiouslly loops

    @property()
    layers: Layer[] = [];

    @property()
    context_layers: Layer[] = [];

    @property()
    onSliderChangeCallback?: (timeSlider: TimeSlider) => void;

    constructor(params: any){
        super(params);
        this.startAnimation = this.startAnimation.bind(this)
        this.stopAnimation = this.stopAnimation.bind(this)
        this.onSliderChange = this.onSliderChange.bind(this)
        this.updateLayers = this.updateLayers.bind(this)
        this.next = this.next.bind(this)
        this.prev = this.prev.bind(this)
    }

    postInitialize() {
        this.updateLayers(null).then(async () => {
            // Does some initial loading of time data
            let newStep: string = (this.value + 1900).toString()

            for(let layer of this.layers){
                if(layer.timeSteps == null) {
                    await layer.getTimeMetaData(newStep)
                }
                layer.setStep(newStep)
            }
            for (let layer of this.context_layers) {
                if(layer.timeSteps == null) {
                    await layer.getTimeMetaData(newStep)
                }
                layer.setStep(newStep)
            }
        })

        
    }

    updateLayers(e: any) {
        return new Promise<void>((resolve) => {
            let handleContext: boolean = true;
            let datetimePromises : Promise<any>[] = []
            let newStep: string = (this.value + 1900).toString()
            for(let layer of this.layers){
                layer.setStep(newStep)
                if (layer.esriLayer?.visible) {
                    datetimePromises.push(layer.getTimeMetaData(newStep))
                }
            }
            if (datetimePromises.length == 0) {
                // If no base layer is selected, but a context layer is, then just treat the first context layer as a base layer
                for (let layer of this.context_layers) {
                    layer.setStep(newStep, )
                    if (layer.esriLayer?.visible) {
                        datetimePromises.push(layer.getTimeMetaData(newStep))
                    }
                }
                handleContext = false
            }
            if (datetimePromises.length == 0) {
                // Just get the time for the first base layer, even when hidden
                if(this.layers[0])
                    datetimePromises.push(this.layers[0].getTimeMetaData(newStep))
            }
            Promise.all(datetimePromises).then( (results) => {
                let datetimeVal: string | null = null
                for(let result of results){
                    if (datetimeVal === null && result.process_datetime != null){
                        datetimeVal = result.process_datetime
                    }

                    if(result.labels?.length) {
                        this.labels = result.labels.map((lbl: string) => dateToString(new Date(lbl), 'hh:mm', false));
                    } 
                }

                if (datetimeVal) {
                    this.datetime = dateParse(datetimeVal,
                        appConfig.config.inputDateFormat,
                        appConfig.config.dateFormat)

                    this.shortdatetime = dateParse(datetimeVal,
                        appConfig.config.inputDateFormat,
                        appConfig.config.shortDateFormat)

                    if(this.labels == null)
                        this.labels = []

                    if (handleContext) {
                        for (let context_layer of this.context_layers) {
                            if (context_layer.esriLayer?.visible) {
                                context_layer.getTimeMetaData(newStep).then(result => {
                                    // difference in 10 minute steps
                                    let diff = Math.floor((dateFromStr(result.process_datetime, appConfig.config.inputDateFormat).getTime() - dateFromStr(datetimeVal ?? '', appConfig.config.inputDateFormat).getTime()) / (10 * 60 * 1000))
                                    // sets the step of this layer to be ofset from the base layer step by diff steps
                                    console.log("Getting step for " + context_layer.name + "... Step is now " + (this.value + 1900 + diff).toString() + " with a diff of " + diff)

                                    context_layer.setStep((this.value + 1900 + diff).toString())
                                })

                            }
                        }
                    }
                } else {
                    this.datetime = 'None'
                    this.shortdatetime = 'None'
                }

                resolve()

                
            }, (rejections) => {
                console.log(rejections)
                resolve()
            })
        })
    }

    onSliderChange(e: any){
        this.value = parseInt(e.target.value)
        this.updateLayers(e)
    }

    next(e? : any){
        this.value++
        if(this.value >= this.max){
            if(this.looping){
                this.value = this.min
            } else {
                this.value = this.max - 1

                if(this.animating){
                    this.stopAnimation(e)
                }
                return
            }
        }

        this.onSliderChange({target: { value: this.value}})
    }

    prev(e? : any){
        this.value--
        if(this.value < this.min){
            if(this.looping){
                this.value = this.max - 1
            } else {
                this.value = this.min
                return
            }

        }

        this.onSliderChange({target: { value: this.value}})
    }

    _sleep(ms: number) : Promise<void>{
        return new Promise( resolve => setTimeout(resolve, ms))
    }

    _startAnimationLoop(){
        this._sleep(this.animatedelay).then(() => {
            if(this.animating){
                this._startAnimationLoop()
            }
        })
        console.log("Animation loop")
        this.next()
    }

    startAnimation(e? : any){
        this.animating = true;
        
        this._startAnimationLoop()
    }

    stopAnimation(e? : any){
        this.animating = false;
    }

    render() {
        
        let labelTags = []

        if (!this.labels || this.labels.length==0){
            this.labels = []

            if (this.datetime && this.datetime.indexOf('---') == -1 && this.datetime != 'None') {
                let currentTime = dateFromStr(this.datetime, appConfig.config.dateFormat)
                let tmpTime: string;
                for (let i: number = this.min; i < this.max; i++) {
                    // @ts-ignore
                    tmpTime = dateToString(new Date(currentTime.valueOf() + (600000 * (i - this.value))), 'hh:mm', false)
                    this.labels.push(tmpTime)
                }
            } else {
                for (let i: number = this.min; i < this.max; i++) {
                    if(i % this.labelstep == 0 || i==this.max-1){
                        if(i<10){
                            this.labels.push('0' + i.toString())
                        } else if(i<100){
                            this.labels.push('' + i.toString())
                        } else {
                            this.labels.push(i.toString())
                        }
                    } else {
                        this.labels.push("")
                    }
                }
            }

            
            
        }

        for(let label of this.labels){
            labelTags.push(<span>{label}</span>)
        }

        let playstopButton = <div class="esri-widget esri-widget--button toggle-button calcite-bgcolor-dark-red" 
                                onclick={this.startAnimation} style="width: 100%;padding-bottom: 15px;display: grid;place-items: center;">
                                <div class="calcite-title-sub" style="width: 100%;display: flex;align-content: center;justify-content: center;">
                                    <span style="font-size: 20px;" class="toggle-button-icon esri-icon-play" aria-label="play icon"></span>
                                    &nbsp;Play
                                </div>
                            </div>

        let shortPlayStopButton = <div class="esri-widget esri-widget--button toggle-button calcite-bgcolor-dark-red"
                                    onclick={this.startAnimation} style="width: 100%;padding-bottom: 15px;display: grid;place-items: center;">
                                <div class="calcite-title-sub" style="width: 100%;display: flex;align-content: center;justify-content: center;">
                                        <span style="font-size: 20px;" class="toggle-button-icon esri-icon-play" aria-label="play icon"></span>
                                    </div>
                                </div>
        if(this.animating){
            playstopButton = <div class="esri-widget esri-widget--button toggle-button calcite-bgcolor-dark-red" 
                                onclick={this.stopAnimation} style="width: 100%;padding-bottom: 15px;display: grid;place-items: center;">
                                <div class="calcite-title-sub" style="width: 100%;display: flex;align-content: center;justify-content: center;">
                                    <span style="font-size: 20px;" class="toggle-button-icon esri-icon-pause" aria-label="pause icon"></span>
                                    &nbsp;Pause
                                </div>
                            </div>

            shortPlayStopButton = <div class="esri-widget esri-widget--button toggle-button calcite-bgcolor-dark-red"
                                onclick={this.stopAnimation} style="width: 100%;padding-bottom: 15px;display: grid;place-items: center;">
                                <div class="calcite-title-sub" style="width: 100%;display: flex;align-content: center;justify-content: center;">
                                    <span style="font-size: 20px;" class="toggle-button-icon esri-icon-pause" aria-label="pause icon"></span>
                                </div>
                            </div>
        }


        return (
            <div class="row row-no-margin">
                <div class="col-md-2 col-sm-3 col-xs-4" style="padding-left: 10px; padding-right: 10px">
                    <div class="row row-no-margin hidden-sm hidden-xs" style="margin-bottom: 5px; ">
                        <div class="col-md-12 calcite-title-sub" style="text-align: center;">
                            { this.datetime }
                        </div>
                    </div>
                    <div class="row row-no-margin hidden-lg hidden-md" style="margin-bottom: 5px; ">
                        <div class="col-xs-12 calcite-title-sub" style="text-align: center;">
                            { this.shortdatetime }
                        </div>
                    </div>
                    <div class="row row-no-margin">
                        <div class="col-md-6 col-md-offset-1 col-sm-7 hidden-xs col-no-padding">
                            { playstopButton }
                        </div>
                        <div class="col-xs-5 visible-xs col-no-padding">
                            {shortPlayStopButton }
                        </div>
                        <div class="col-md-5 col-sm-5 col-xs-7 col-no-padding">
                            <span style="font-size: 22px;color: white;" class="toggle-button-icon esri-icon-left-triangle-arrow white-text" 
                                   onclick={this.prev} aria-label="prev icon"></span>
                            <span style="font-size: 22px;color: white;" class="toggle-button-icon esri-icon-right-triangle-arrow white-text" 
                                   onclick={this.next} aria-label="next icon"></span>
                        </div>
                    </div>
                </div>
                <div class="col-md-10 col-sm-9 col-xs-8">
                    <div class={this.classes(CSS.base)}>
                        <div id="timesliderLabels" class="slider-labels calcite-title-sub" style="width: 100%; display: flex; justify-content: space-between">
                            {labelTags}
                        </div>
                        <div id="rangeWrapper">
                            <input id="timeslider" class="calcite-slider" type="range" 
                                min={this.min} max={this.max-1} onchange={this.onSliderChange}
                                value={this.value} step={this.step} style="width: 100%;" />
                        </div>
                    </div>
                </div>
            </div>
            
        );
    }

    destroy(){

    }

}

export default TimeSlider

